import React, { useEffect, useReducer, useCallback, useState } from 'react'
import HttpRequest from './HttpRequest';
import {Modal, ModalBody, Progress} from 'reactstrap';
import Certificate from '../crypto/Certificate'
import Signature from '../crypto/Signature'
import ParsedCertificate from '../crypto/ParsedCertificate'
import {retryPromiseUntilSuccess, appendToArray, wait, waitForAsyncResult} from '../utils/toolbox';
import ImageGenerator from '../utils/ImageGenerator'
import { useDispatch, useSelector } from "react-redux";
import {
    loadReport,
    addHtmlSignatureImg,
    setPickingSignatureStyle,
    setSigning,
    addTask,
    removeTask,
    setLocalSignaures,
    setVerifyingIdentity, setConfirmEndorsement, clearActiveSticker
} from "../redux/actions";
import Spinner from '../shared/Spinner'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import axios from 'axios'
import SolidButton from './SolidButton';
import {usePalette, ImgSuccess, ImgFirmando, useAutoSignAll} from './Customizables';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import queryString from 'query-string';

import { useTranslation } from 'react-i18next';
import PinInput from './PinInput';
import useGeoLocate from '../hooks/useGeoLocate';
import GeoLocateOverlay from './GeoLocateOverlay';
import useAttachments from '../hooks/useAttachments';
import FlexColumn from './FlexColumn';
import useHasAccount from '../hooks/useHasAccount';
import PropTypes from "prop-types";


const ACTIONS = {
    ADD_MSG: 'ADD_MSG',
    CLEAR_MSG: 'CLEAR_MSG',
    SET_STATUS: 'SET_STATUS',
    RESET: 'RESET'
}

const MODAL_STATUS = {
    DEFAULT: 'DEFAULT',
    SIGNING: 'SIGNING',
    SIGNED: 'SIGNED',
    OCSP_ERROR: 'OCSP_ERROR',
    OCSP_INVALID_FIRMAMEX: 'OCSP_INVALID_FIRMAMEX',
    OCSP_INVALID_PSC: 'OCSP_INVALID_PSC',
    DOC_CHANGED: 'DOC_CHANGED',
    ALREADY_SIGNED: 'ALREADY_SIGNED',
    RELOAD_ERROR: 'RELOAD_ERROR',
    OCSP_TIMESTAMP_ERROR: 'OCSP_TIMESTAMP_ERROR',
    OCSP_INVALID: 'OCSP_INVALID',
    IDENTITY_VERIFICATION_ERROR: 'IDENTITY_VERIFICATION_ERROR',
    ENDORSEMENT_CANCELED: 'ENDORSEMENT_CANCELED',
    BAD_SIGNATURE_POSITION: 'BAD_SIGNATURE_POSITION'
}

const defaultState = {
    messages: [],
    status: MODAL_STATUS.DEFAULT,
    currentSignatures: 0
}

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.ADD_MSG: {
            if (state.messages.find(m => m === action.payload.message)) {
                return {
                    ...state
                }
            }

            return {
                ...state,
                messages: [...state.messages, action.payload.message]
            }
        }
        case ACTIONS.CLEAR_MSG: {
            return {
                ...state,
                messages: []
            }
        }
        case ACTIONS.SET_STATUS: {
            return {
                ...state,
                messages: action.payload.messages || state.messages,
                errors: action.payload.errors,
                status: action.payload.status,
                currentSignatures: action.payload.currentSignatures
            }
        }
        case ACTIONS.RESET: {
            return defaultState;
        }
        default: {
            return state;
        }
    }
}

function loadAxios(jwt) {
    axios.interceptors.request.handlers = [];
    axios.interceptors.request.use(
        (config) => {
            if(!config.headers['Authorization']) {
                config.headers['Authorization'] = `Bearer ${jwt}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );
}

function isFirmamexCert(signupAuthority) {
    return signupAuthority !== 'Cecoban' &&
        signupAuthority !== 'SAT' &&
        signupAuthority !== 'SeguriData'
}

function InvalidOCSPMessage({ t, status }) {
    const message = t(status === MODAL_STATUS.OCSP_INVALID_FIRMAMEX ? 'invalidOCSPFirmamex' : 'invalidOCSPPSC');
    return <p>{message}</p>
}


function IdentityVerificationErrors({t, errors}) {
    if(errors?.length > 0) {
        return (
            <>
                <p style={{fontWeight: 'bold'}}>
                    {t('identity verification error list')}
                </p>
                {
                    errors.map(e =>
                        <div key={e.message}>
                            {t(`JUMIO_${e.category}_${e.message}`)}
                        </div>
                    )
                }
            </>
        )
    }
    return (
        <p style={{fontWeight: 'bold'}}>
            {t('identity verification error desc')}
        </p>
    )
}
IdentityVerificationErrors.propTypes = {
    t: PropTypes.func.isRequired,
    errors: PropTypes.arrayOf(
        PropTypes.shape({
            category: PropTypes.string.isRequired,
            message: PropTypes.string.isRequired
        })
    ),
}


export default function WithSingleUseCertificate({ isOpen, onComplete, onCancel }) {

    const { t } = useTranslation()
    const signableStickers = useSelector(state => state.signableStickers);
    const { translate } = useSelector(state => state.config)
    const { document, report } = useSelector(state => state.document)
    const userSession = useSelector(state => state.session.current);
    const { hideConfirmation } = useSelector(state => state.customizables)
    const [{ messages, status, errors, currentSignatures }, modalDispatch] = useReducer(reducer, defaultState)

    const dispatch = useDispatch();
    const palette = usePalette();

    const [feedbackForm, setFeedbackForm] = useState(null)

    const [requestingPin, setRequestingPin] = useState(false);
    const [signatureProcess, setSignatureProcess] = useState(0);
    const [toSignCount, setToSignCount] = useState(0);
    const autoSignAll = useAutoSignAll();

    const [requestLocation, locationAllowed] = useGeoLocate()
    const [hasAttachments, attachmentsStatus, attachments, downloadAttachments] = useAttachments();
    const [signatureMetaId, setSignatureMetaId] = useState(null)

    const hasAccount = useHasAccount()


    const handleClickCancel = useCallback(function () {
        dispatch(setSigning({
            signing: {
                working: false,
                data: {}
            }
        }))
        onCancel();
    }, [dispatch, onCancel])

    useEffect(() => {

        if (isOpen && signableStickers.active && signableStickers.signableStickers) {
            const sticker = signableStickers.signableStickers.find(s => s.id === signableStickers.active.id).sticker;
            if (status === MODAL_STATUS.DEFAULT && sticker.authority.singleUse) {
                modalDispatch({
                    type: ACTIONS.SET_STATUS,
                    payload: {
                        status: MODAL_STATUS.SIGNING,
                        currentSignatures: report.firmas.length
                    }
                })
            }
        }


    }, [isOpen, signableStickers, status, report.firmas])

    useEffect(() => {


        function addToMsgs(message, data) {

            if(message === 'Todo firmado') {
                setTimeout(() => {
                    if(report.boxes.filter(b => !b.signed).length === 1) {
                        if(hasAttachments) {
                            downloadAttachments();
                        }
                    }
                    modalDispatch({
                        type: ACTIONS.SET_STATUS,
                        payload: {
                            status: MODAL_STATUS.SIGNED,
                            currentSignatures
                        }
                    })
                }, 600);
                return;
            }


            if (message === 'Firmado') {
                setSignatureMetaId(data?.signatureMetaId)
                if(hideConfirmation && !autoSignAll) {
                    handleClickOnComplete(null, data?.signatureMetaId);
                } else {
                    setTimeout(() => {
                        if(signableStickers && signableStickers.signableStickers?.length === 1) {
                            if(report.boxes.filter(b => !b.signed).length === 1) {
                                if(hasAttachments) {
                                    downloadAttachments();
                                }
                            }
                            modalDispatch({
                                type: ACTIONS.SET_STATUS,
                                payload: {
                                    status: MODAL_STATUS.SIGNED,
                                    currentSignatures
                                }
                            })
                        } else if(!autoSignAll) {
                            handleClickOnComplete(null, data?.signatureMetaId);
                        }
                    }, 600);
                }
            } else {
                modalDispatch({
                    type: ACTIONS.ADD_MSG,
                    payload: {
                        message
                    }
                })
            }
        }

        async function signDocument() {
            let signaturesCount = Math.max(report.firmas.length, currentSignatures);

            if(!signableStickers.active) {
                return;
            }

            const signable = autoSignAll ?
                signableStickers.signableStickers
                : [signableStickers.signableStickers.find(ss => ss.id === signableStickers.active.id)];

            const count = signable.length;
            let signed = 0;

            setSignatureMetaId(null)
            setSignatureProcess(0);
            setToSignCount(count);

            let lastResult = null;
            for(let sticker of signable) {
                modalDispatch({
                    type: ACTIONS.CLEAR_MSG
                })
                if(signed === 0) {
                    dispatch(addTask('reloading signatures', 'Actualizando documento'))
                } else {
                    dispatch(addTask('reloading signatures', 'Actualizando documento para siguiente firma'))
                }
                if(lastResult?.sigDataId) {
                    await waitForAsyncResult(
                        async () => {
                            const res = await HttpRequest.get(`/browser/sign/local/${lastResult?.sigDataId}/status`)
                            return res?.data?.saved
                        },
                        1000
                    )
                }
                const localSignatures = await HttpRequest.get(`/pdf/signatures/original/${report.ticket}`, {
                    responseType: 'arraybuffer'
                })
                dispatch(removeTask('reloading signatures'))
                lastResult = await signupFromSticker(sticker.sticker, localSignatures.data, signaturesCount++, lastResult?.img);
                if(!lastResult) {
                    return;
                }
                signed++;
                setSignatureProcess((signed/count) * 100);
            }

            if(autoSignAll) {
                addToMsgs('Todo firmado')
            }


        }

        async function signupFromSticker(sticker, localSignatures, currentSignatures, lastImg) {

            const certType = sticker.authority.dataType === 'PHONE' ? 'phone' : 'email';

            let endorsementError = false;
            try {
                const res = await HttpRequest.get(`/browser/${report.ticket}/endorsement/${sticker._id}`)
                if(res.data) {
                    const endorsement = res.data;
                    await new Promise((resolve, reject) => {
                        dispatch(setConfirmEndorsement({
                            working: true, endorsement, resolve, reject
                        }))
                    })
                }
            } catch (e) {
                if(e?.response?.status !== 404) {
                    // 404 means there is no endorsement
                    endorsementError = true;
                }
            } finally {
                dispatch(setConfirmEndorsement({
                    working: false,
                }))
            }

            if(endorsementError) {
                modalDispatch({
                    type: ACTIONS.SET_STATUS,
                    payload: {
                        status: MODAL_STATUS.ENDORSEMENT_CANCELED
                    }
                })
                return null;
            }

            let latLng = {};
            if (sticker.requireLocation) {
                latLng = await requestLocation()
            }

            if(sticker.requireIdentityVerification) {
                let identityResult = null;
                try {
                    identityResult = await new Promise((resolve, reject) => {
                        dispatch(setVerifyingIdentity({
                            working: true,
                            boxId: sticker._id,
                            resolve, reject
                        }))
                    })
                    addToMsgs('verifying_identity_data');
                } catch(e) {
                }
                dispatch(setVerifyingIdentity({
                    working: false,
                    boxId: null,
                    resolve: null,
                    reject: null
                }))
                let verificationResult = null;
                if(identityResult) {
                    try {
                        verificationResult = await waitUntilIdentityConfirmation(identityResult);
                    }
                    catch (e) {}
                }
                if(!verificationResult || verificationResult.status !== 'SUCCESS') {
                    modalDispatch({
                        type: ACTIONS.SET_STATUS,
                        payload: {
                            status: MODAL_STATUS.IDENTITY_VERIFICATION_ERROR,
                            errors: verificationResult?.errors
                        }
                    })
                    return null;
                }
            }

            let pin = null;
            if (sticker.authority.requirePin) {
                pin = await validatePin(sticker._id, addToMsgs);
            }

            addToMsgs('Generando tu par de llaves')

            const csrData = await retryPromiseUntilSuccess(Certificate.generateCsr, {
                certType,
                name: sticker.data,
                email: sticker.email
            })
            addToMsgs('Firmando solicitud de certificado')

            const signed = await Certificate.signCsr(csrData, 'firmamex-temp',
                sticker.data,
                sticker.authority,
                sticker.authority.dataType,
                null,
                null,
                report.signerLink ? report.signerLink.link : null,
                report.ticket,
                report.setLink ? report.setLink.link : null,
                pin)

            const verificationResult = await Certificate.waitUntilVerified(signed);

            loadAxios(verificationResult.jwt)

            const cert = await Certificate.certificateFromB64(verificationResult.cert);
            addToMsgs('Verificando registro')
            const checkoutResponse = await HttpRequest.post('/signup/checkout', {
                clientData: {},
                customer: {
                    email: sticker.email
                }
            })
            addToMsgs('Obteniendo imagen de firma')
            const parsedCert = ParsedCertificate.fromPem(cert);
            const fullDoc = appendToArray(document, localSignatures);

            let img;
            if(lastImg && autoSignAll) {
                if(lastImg.imageType === 'hash') {
                    img = await ImageGenerator.generateImageForSticker(sticker, report.ticket);
                } else {
                    img = lastImg
                }
            } else if (!sticker.imageType || sticker.imageType.includes('any')) {
                img = await new Promise((resolve, reject) => {
                    dispatch(setPickingSignatureStyle({
                        pickingSignatureStyle: {
                            working: true,
                            data: {
                                commonName: parsedCert.getCommonName(),
                                issuerName: parsedCert.getIssuerName(),
                                cancelable: false,
                                requestedImageType: sticker.imageType,
                                stickerId: sticker._id,
                                resolve,
                                reject
                            }
                        }
                    }))
                })
                dispatch(setPickingSignatureStyle({
                    pickingSignatureStyle: {
                        working: false,
                        data: {}
                    }
                }))
            } else {
                img = await ImageGenerator.generateImageForSticker(sticker, report.ticket);
            }

            if (!img) {
                handleClickCancel();
                return null;
            }

            await HttpRequest.post('/browser/sticker/img', {
                b64Image: img.data,
                boxId: sticker._id
            })

            const {error, sigDataId} = await Signature.sign({
                report,
                certData: {
                    chain: checkoutResponse.data.data.certChain,
                    pem: cert
                },
                latLng,
                pkcs8Pem: csrData.pkcs8Pem,
                pass: 'firmamex-temp',
                original: new Blob([fullDoc], { type: 'application/pdf' }),
                sticker: sticker,
                signatureImg: img,
                onProgress: addToMsgs,
                signatureCount: currentSignatures,
                pin
            })

            if (error) {
                switch (error) {
                    case 'bad_signature_position': {
                        modalDispatch({
                            type: ACTIONS.SET_STATUS,
                            payload: {
                                status: MODAL_STATUS.BAD_SIGNATURE_POSITION
                            }
                        })
                        break;
                    }
                    case 'ocsp_timestamp_error': {
                        modalDispatch({
                            type: ACTIONS.SET_STATUS,
                            payload: {
                                status: MODAL_STATUS.OCSP_TIMESTAMP_ERROR
                            }
                        })
                        break;
                    }
                    case 'invalid_cert_ocsp': {
                        modalDispatch({
                            type: ACTIONS.SET_STATUS,
                            payload: {
                                status: isFirmamexCert(userSession.x509.signupAuthority) ?
                                    MODAL_STATUS.OCSP_INVALID_FIRMAMEX :
                                    MODAL_STATUS.OCSP_INVALID_PSC,
                                currentSignatures
                            }
                        })
                        break;
                    }
                    case 'ocsp_error': {

                        if(isFirmamexCert(userSession.x509.signupAuthority)) {
                            modalDispatch({
                                type: ACTIONS.SET_STATUS,
                                payload: {
                                    status: MODAL_STATUS.OCSP_INVALID_FIRMAMEX,
                                    currentSignatures
                                }
                            })
                        } else {
                            modalDispatch({
                                type: ACTIONS.SET_STATUS,
                                payload: {
                                    status: MODAL_STATUS.OCSP_ERROR,
                                    currentSignatures
                                }
                            })
                        }

                        break;
                    }
                    case 'doc_changed': {
                        modalDispatch({
                            type: ACTIONS.SET_STATUS,
                            payload: {
                                status: MODAL_STATUS.DOC_CHANGED,
                                currentSignatures
                            }
                        })
                        break;
                    }
                    case 'already_signed': {
                        modalDispatch({
                            type: ACTIONS.SET_STATUS,
                            payload: {
                                status: MODAL_STATUS.ALREADY_SIGNED,
                                currentSignatures
                            }
                        })
                        break;
                    }
                    default: {
                        modalDispatch({
                            type: ACTIONS.SET_STATUS,
                            payload: {
                                status: MODAL_STATUS.RELOAD_ERROR,
                                currentSignatures
                            }
                        })
                        break;
                    }
                }
            } else {
                dispatch(addHtmlSignatureImg({ ...sticker, signatureImg: img }))
            }

            return {
                img, sigDataId
            }


        }

        if (status === MODAL_STATUS.SIGNING && signableStickers.active) {
            signDocument();
        }

    }, [status, dispatch, document, handleClickCancel, report,
        signableStickers, userSession.jwt, translate, currentSignatures, t, modalDispatch])

    useEffect(() => {

        if (!isOpen) {
            modalDispatch({
                type: ACTIONS.RESET
            })
        }

    }, [isOpen])


    async function waitUntilIdentityConfirmation({requestId}) {
        let status = null;
        while(!status) {
            await wait(800)
            const res = await HttpRequest.get(`/browser/kyc/verification/${requestId}/status`);
            if(res.data.length > 0) {
                const lastStatus = res.data[res.data.length - 1];
                if(lastStatus?.status && lastStatus.status !== 'PENDING') {
                    status = lastStatus;
                }
            }
        }
        return status;
    }

    async function waitUntilHasPin() {
        while(!sessionStorage.getItem('pin')) {
            await wait(800);
        }
    }

    async function validatePin(stickerId, addToMsgs) {
        let requestPin = true;
        let pin = sessionStorage.getItem('pin')
        if (pin) {
            addToMsgs('Verificando OTP')
            const response = await HttpRequest.get(`/pin/${pin}/verify/${stickerId}`)
            if (response.data.valid) {
                requestPin = false;
            }
        }

        if (requestPin) {
            sessionStorage.removeItem('pin')
            addToMsgs('Enviando OTP')
            setRequestingPin(true)
            await waitUntilHasPin()
            pin = await validatePin(stickerId, addToMsgs)
        }

        return pin;
    }

    async function handleClickOnComplete(e, sigDataId) {

        const hasRemainingStickers = !autoSignAll && signableStickers.signableStickers.find(s => s.id !== signableStickers.active?.id);

        if (feedbackForm != null) {
            HttpRequest.post('/feedback', {
                ...feedbackForm, frmxId: report.ticket
            });
            setFeedbackForm(null)
        }

        if (userSession.jwt) {
            loadAxios(userSession.jwt)
        }

        debugger
        await dispatch(loadReport(report.ticket, report.queryString, sigDataId ?? signatureMetaId))
        dispatch(setSigning({
            signing: {
                working: false,
                data: {}
            }
        }))


        const qs = queryString.parse(window.location.search);
        if(!hasRemainingStickers && qs.fromUrl && qs.fromUrl.indexOf('javascript') === -1) {
            let decodedUrl = decodeURIComponent(qs.fromUrl);
            if(!decodedUrl.includes('doc=')) {
                const splitted = decodedUrl.split('?');
                const fromUrlQuery = queryString.parse(splitted[1] || '');
                fromUrlQuery.doc = report.ticket;
                decodedUrl = queryString.stringifyUrl({url: splitted[0], query: fromUrlQuery})
            }
            window.location.href = decodeURIComponent(decodedUrl);
        } else if(qs.callback && window.opener) {
            window.onbeforeunload = undefined;
            window.opener.postMessage(JSON.stringify({
                signed: true,
                doc: report.ticket
            }), '*');
            if(!hasRemainingStickers) {
                window.close();
            }
        } else if(window.parent && window.self !== window.top) {
            window.parent.postMessage({
                signed: true,
                doc: report.ticket
            }, '*')
        }
    }

    function handleChangeFeedback(feedback) {
        setFeedbackForm(feedback)
    }

    function handleOkErrorClick() {
        dispatch(setSigning({
            signing: {
                working: false,
                data: {
                    canceled: true
                }
            }
        }))
        dispatch(clearActiveSticker())
    }

    async function restartSignature() {
        dispatch(addTask('reloading signatures', 'Actualizando documento'))
        const signatures = await HttpRequest.get(`/pdf/signatures/original/${report.ticket}`, {
            responseType: 'arraybuffer'
        })
        dispatch(setLocalSignaures({
            localSignatures: signatures.data
        }))
        const response = await HttpRequest.get('/browser/report/' + report.ticket + '?t=' + new Date().getTime())

        dispatch(removeTask('reloading signatures'))
        modalDispatch({
            type: ACTIONS.SET_STATUS,
            payload: {
                messages: ['Generando tu par de llaves'],
                status: MODAL_STATUS.SIGNING,
                currentSignatures: response.data.firmas.length
            }
        })

    }

    function handlePinSuccess(pin) {
        sessionStorage.setItem('pin', pin)
        setRequestingPin(false);
    }

    return (
        <React.Fragment>

            <Modal isOpen={isOpen}>
                <ModalBody style={{ textAlign: 'center', paddingTop: '20px' }}>
                    {
                        status === MODAL_STATUS.OCSP_TIMESTAMP_ERROR &&
                        <div>
                            <h3 style={{ color: palette.accentText }}>
                                {t('errorSigning')}
                            </h3>
                            <br />
                            <p>{t('errorSigningOcspTimestamp')}</p>
                            <SolidButton onClick={() => { window.location.reload() }}>{t('accept')}</SolidButton>
                            <br />
                        </div>
                    }
                    {
                        status === MODAL_STATUS.BAD_SIGNATURE_POSITION ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('bad signature position error')}
                                </h3>
                                <br />
                                <p>{t('bad signature position error desc')}</p>
                                <SolidButton onClick={() => { window.location.reload() }}>
                                    {t('Aceptar')}
                                </SolidButton>
                                <br />
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.RELOAD_ERROR ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('errorSigning')}
                                </h3>
                                <br />
                                <p>{t('errorSigning2')}</p>
                                <p>
                                    {t('errorSigning3')}
                                </p>
                                <p>
                                    {t('errorSigning4')}
                                </p>
                                <SolidButton onClick={() => { window.location.reload() }}>
                                    {t('Aceptar')}
                                </SolidButton>
                                <br />
                            </div> : null
                    }

                    {
                        status === MODAL_STATUS.OCSP_ERROR ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('errorOCSP')}
                                </h3>
                                <br />
                                <p>{t('errorOCSP2')}</p>
                                <p>{t('errorOCSP3')}</p>
                                <SolidButton onClick={handleOkErrorClick}>{t('accept')}</SolidButton>
                                <br />
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.ENDORSEMENT_CANCELED ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('endorsement canceled')}
                                </h3>
                                <br />
                                <p style={{fontWeight:'bold'}}>
                                    {t('endorsement canceled error')}
                                </p>
                                <SolidButton onClick={handleOkErrorClick}>{t('accept')}</SolidButton>
                                <br />
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.IDENTITY_VERIFICATION_ERROR ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('identity verification error')}
                                </h3>
                                <br />
                                <IdentityVerificationErrors
                                    t={t}
                                    errors={errors}>
                                </IdentityVerificationErrors>
                                <SolidButton onClick={handleOkErrorClick}>{t('accept')}</SolidButton>
                                <br />
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.OCSP_INVALID_FIRMAMEX ||
                        status === MODAL_STATUS.OCSP_INVALID_PSC ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('invalidOCSP')}
                                </h3>
                                <br />
                                <InvalidOCSPMessage t={t} status={status} />
                                <SolidButton onClick={handleOkErrorClick}>{t('accept')}</SolidButton>
                                <br />
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.DOC_CHANGED ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('documentChanged')}
                                </h3>
                                <br />
                                <p>{t('documentChanged2')}</p>
                                <p>{t('documentChanged3')}</p>
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.ALREADY_SIGNED ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>

                                </h3>
                                <br />
                                <p>{t('sameSignature2')}</p>
                                <SolidButton onClick={() => { window.location.reload() }}>{t('accept')}</SolidButton>
                                <br />
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.SIGNED ?
                            <div>
                                <h3 style={{ color: palette.accentText }}>
                                    {t('documentSigned')} <br />{t('successfully')}
                                </h3>
                                <br />
                                <ImgSuccess></ImgSuccess>
                                <br/>
                                {
                                    // tiene attachments y se firmo el ultimo sticker
                                    hasAttachments && report.boxes.filter(b => !b.signed).length === 1 ?
                                        <div>
                                            <br/>
                                            <p>{t(attachmentsStatus)}</p>
                                            {
                                                attachments && attachments.length > 0 ?
                                                    <div>
                                                        <div>
                                                            {t('download attachments here')}
                                                        </div>
                                                        {
                                                            attachments.map(f => (
                                                                <div key={f}>
                                                                    <a href={`${HttpRequest.backend}/pdf/attachments/${report.ticket}/${f}`}>{f}</a>
                                                                </div>
                                                            ))
                                                        }
                                                    </div> : null
                                            }
                                        </div> : null
                                }
                                <br/>
                                <FlexColumn alignItems={'center'}>
                                    <p>
                                        {t('all signatures done for document')}
                                    </p>
                                    <SolidButton color="secondary" onClick={handleClickOnComplete}>{t('ok')}</SolidButton>
                                </FlexColumn>
                                <br/>
                                {
                                    report.inFlow && (!report.allSigned || report.boxes.filter(b => !b.signed) >= 1) ?
                                        <p style={{margin:'0 30px'}}>
                                            {t('documentInFlow')}
                                        </p> : null
                                }
                                {
                                    // askForFeedback && (signableStickers.signableStickers && signableStickers.signableStickers.length === 1) &&
                                    //     (window.location.href.includes('docmage') || window.location.href.includes('firmamex.com') || window.location.href.includes('xpertal')) ?
                                    // <UserFeedbackForm onChange={handleChangeFeedback}></UserFeedbackForm> : null
                                }
                            </div> : null
                    }
                    {
                        status === MODAL_STATUS.SIGNING ?
                            <div className='imageDialog'>
                                <ImgFirmando></ImgFirmando>
                                <div className="imageDialogSeparator"></div>
                                <p className='imageDialogTitle'>
                                    {t('signing')}
                                </p>
                                <br/>
                                {
                                    messages.length > 0 ?
                                        <div dangerouslySetInnerHTML={
                                            {__html: t(messages[messages.length - 1], {interpolation: {escapeValue: false}})}
                                        }>
                                        </div>
                                    : null
                                }
                                {
                                    toSignCount > 1 ?
                                        <div>
                                            <br/>
                                            <Progress value={signatureProcess}></Progress>
                                        </div> : null
                                }
                            </div>
                            : null
                    }
                    <br/>
                    <TransitionGroup>
                        {
                            requestingPin ?
                                <CSSTransition timeout={300} classNames="move" unmountOnExit>
                                    <PinInput
                                        ticket={report.ticket}
                                        sticker={signableStickers.signableStickers.find(s => s.id === signableStickers.active.id).sticker}
                                        onSuccess={handlePinSuccess}
                                        onCancel={handleOkErrorClick}></PinInput>
                                </CSSTransition> : null
                        }
                    </TransitionGroup>
                    <br />
                    <Spinner show={status === MODAL_STATUS.SIGNING}></Spinner>
                </ModalBody>
                {
                    status === MODAL_STATUS.DOC_CHANGED ?
                        <ModalFooter>
                            <SolidButton onClick={restartSignature}>{t('confirm')}</SolidButton>
                            <SolidButton color="secondary" onClick={handleOkErrorClick}>{t('cancel')}</SolidButton>
                        </ModalFooter> : null
                }
            </Modal>
            <GeoLocateOverlay isOpen={locationAllowed === false}></GeoLocateOverlay>
        </React.Fragment>
    )

}
